(() => {
  const MIN_ASPECT = 0.5625;
  const MAX_ASPECT = 1.7778;

  function clampAspect(value) {
    const aspect = Number(value);
    if (!Number.isFinite(aspect) || aspect <= 0) return 0.75;
    return Math.min(MAX_ASPECT, Math.max(MIN_ASPECT, aspect));
  }

  function syncImageAspect(image) {
    const card = image.closest('[data-case-card]');
    if (!card || !image.naturalWidth || !image.naturalHeight) return;
    card.style.setProperty('--case-aspect', clampAspect(image.naturalWidth / image.naturalHeight));
  }

  function watchImageAspect(image) {
    if (image.complete) syncImageAspect(image);
    image.addEventListener('load', () => syncImageAspect(image), {once: true});
  }

  function addText(parent, tag, className, value) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    element.textContent = value;
    parent.appendChild(element);
    return element;
  }

  function stars(value) {
    const rating = Math.max(1, Math.min(5, Math.round(Number(value) || 0)));
    return `${'★'.repeat(rating)}${'☆'.repeat(5 - rating)}`;
  }

  function formatDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(document.documentElement.lang || 'zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  }

  function safeProductUrl(handle) {
    const normalized = String(handle || '').trim();
    return /^[a-z0-9][a-z0-9-]*$/i.test(normalized)
      ? `/products/${encodeURIComponent(normalized)}#product-reviews`
      : '';
  }

  function closeCard(card) {
    if (!card) return;
    card.classList.remove('is-open');
    card.querySelector('[data-case-overlay-trigger]')?.setAttribute('aria-expanded', 'false');
    card.querySelector('[data-case-overlay]')?.setAttribute('aria-hidden', 'true');
  }

  function openCard(card, rail) {
    rail.querySelectorAll('.case-slide.is-open').forEach((item) => {
      if (item !== card) closeCard(item);
    });
    card.classList.add('is-open');
    card.querySelector('[data-case-overlay-trigger]')?.setAttribute('aria-expanded', 'true');
    card.querySelector('[data-case-overlay]')?.setAttribute('aria-hidden', 'false');
  }

  function renderReview(review, index) {
    const card = document.createElement('article');
    const overlayId = `mangorace-review-showcase-${String(review.id || index).replace(/[^a-z0-9_-]/gi, '')}`;
    const aspect = clampAspect(Number(review.media?.width) / Number(review.media?.height));
    card.className = 'case-slide case-slide--review';
    card.dataset.caseCard = '';
    card.style.setProperty('--case-aspect', aspect);

    const media = document.createElement('div');
    media.className = 'case-slide__media';
    const image = document.createElement('img');
    image.src = review.media.url;
    image.alt = `${review.productTitle || 'MangoRace product'} customer photo`;
    image.loading = 'lazy';
    image.decoding = 'async';
    watchImageAspect(image);
    media.appendChild(image);
    card.appendChild(media);

    const trigger = document.createElement('button');
    trigger.className = 'case-slide__review-trigger';
    trigger.type = 'button';
    trigger.dataset.caseOverlayTrigger = '';
    trigger.setAttribute('aria-label', `View review from ${review.customerDisplayName || 'customer'}`);
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', overlayId);
    card.appendChild(trigger);

    const overlay = document.createElement('div');
    overlay.className = 'case-slide__overlay';
    overlay.id = overlayId;
    overlay.dataset.caseOverlay = '';
    overlay.setAttribute('aria-hidden', 'true');

    const close = document.createElement('button');
    close.className = 'case-slide__close';
    close.type = 'button';
    close.dataset.caseOverlayClose = '';
    close.setAttribute('aria-label', 'Close review');
    close.textContent = '×';
    overlay.appendChild(close);

    const identity = document.createElement('div');
    identity.className = 'case-slide__identity';
    addText(identity, 'strong', 'case-slide__buyer', review.customerDisplayName || 'Verified customer');
    const rating = addText(identity, 'span', 'case-slide__stars', stars(review.rating));
    rating.setAttribute('aria-label', `${review.rating}-star review`);
    overlay.appendChild(identity);

    if (review.verifiedPurchase) {
      addText(overlay, 'span', 'case-slide__verified', 'Verified purchase');
    }
    if (review.title) addText(overlay, 'h3', '', review.title);
    addText(overlay, 'p', 'case-slide__review-body', review.body || '');

    const footer = document.createElement('div');
    footer.className = 'case-slide__review-footer';
    const product = document.createElement('span');
    addText(product, 'strong', '', review.productTitle || 'MangoRace product');
    const date = formatDate(review.createdAt);
    if (date) addText(product, 'small', '', date);
    footer.appendChild(product);

    const productUrl = safeProductUrl(review.productHandle);
    if (productUrl) {
      const link = document.createElement('a');
      link.className = 'case-slide__product-link';
      link.href = productUrl;
      link.textContent = 'View product';
      footer.appendChild(link);
    }
    overlay.appendChild(footer);
    card.appendChild(overlay);
    return card;
  }

  function bindCaseInteractions(section, rail) {
    const hoverPointer = window.matchMedia('(hover: hover) and (pointer: fine)');

    rail.addEventListener('pointerover', (event) => {
      if (!hoverPointer.matches) return;
      const card = event.target.closest('.case-slide');
      if (card && rail.contains(card)) openCard(card, rail);
    });

    rail.addEventListener('pointerout', (event) => {
      if (!hoverPointer.matches) return;
      const card = event.target.closest('.case-slide');
      if (!card) return;
      const nextTarget = event.relatedTarget;
      if (nextTarget instanceof Node && card.contains(nextTarget)) return;
      closeCard(card);
    });

    rail.addEventListener('focusin', (event) => {
      if (!hoverPointer.matches) return;
      const card = event.target.closest('.case-slide');
      if (card && rail.contains(card)) openCard(card, rail);
    });

    rail.addEventListener('focusout', (event) => {
      if (!hoverPointer.matches) return;
      const card = event.target.closest('.case-slide');
      if (!card) return;
      const nextTarget = event.relatedTarget;
      if (nextTarget instanceof Node && card.contains(nextTarget)) return;
      closeCard(card);
    });

    rail.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-case-overlay-trigger]');
      if (trigger) {
        if (hoverPointer.matches) return;
        const card = trigger.closest('.case-slide');
        if (card?.classList.contains('is-open')) closeCard(card);
        else if (card) openCard(card, rail);
        return;
      }
      const close = event.target.closest('[data-case-overlay-close]');
      if (close) closeCard(close.closest('.case-slide'));
    });

    document.addEventListener('click', (event) => {
      if (!section.contains(event.target)) {
        rail.querySelectorAll('.case-slide.is-open').forEach(closeCard);
      }
    });
    section.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        rail.querySelectorAll('.case-slide.is-open').forEach(closeCard);
      }
    });
  }

  function initCaseCarousel(section, rail) {
    if (rail.dataset.caseCarouselReady === 'true') return;
    rail.dataset.caseCarouselReady = 'true';

    const controls = section.querySelector('[data-case-carousel-controls]');
    const previous = section.querySelector('[data-case-previous]');
    const next = section.querySelector('[data-case-next]');
    const position = section.querySelector('[data-case-position]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let activeIndex = 0;
    let scrollFrame = 0;
    let autoRotateTimer = 0;
    let paused = false;

    function cards() {
      return Array.from(rail.querySelectorAll('.case-slide'));
    }

    function paintActive(index) {
      const items = cards();
      if (items.length === 0) return;
      activeIndex = Math.max(0, Math.min(index, items.length - 1));
      items.forEach((card, cardIndex) => {
        card.classList.toggle('is-carousel-active', cardIndex === activeIndex);
        card.setAttribute('aria-current', cardIndex === activeIndex ? 'true' : 'false');
      });
      if (position) position.textContent = `${activeIndex + 1} / ${items.length}`;
      if (controls) controls.hidden = items.length < 2;
    }

    function centerCard(index, behavior = 'smooth') {
      const items = cards();
      if (items.length === 0) return;
      const normalizedIndex = (index + items.length) % items.length;
      const card = items[normalizedIndex];
      paintActive(normalizedIndex);
      rail.scrollTo({
        left: card.offsetLeft - ((rail.clientWidth - card.offsetWidth) / 2),
        behavior: reducedMotion.matches ? 'auto' : behavior
      });
    }

    function syncFromScroll() {
      const items = cards();
      if (items.length === 0) return;
      const railCenter = rail.scrollLeft + (rail.clientWidth / 2);
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      items.forEach((card, index) => {
        const distance = Math.abs((card.offsetLeft + (card.offsetWidth / 2)) - railCenter);
        if (distance < closestDistance) {
          closestIndex = index;
          closestDistance = distance;
        }
      });
      paintActive(closestIndex);
    }

    function startAutoRotate() {
      window.clearInterval(autoRotateTimer);
      if (reducedMotion.matches || cards().length < 2) return;
      autoRotateTimer = window.setInterval(() => {
        if (!paused && !rail.querySelector('.case-slide.is-open')) centerCard(activeIndex + 1);
      }, 5200);
    }

    previous?.addEventListener('click', () => {
      centerCard(activeIndex - 1);
      startAutoRotate();
    });
    next?.addEventListener('click', () => {
      centerCard(activeIndex + 1);
      startAutoRotate();
    });
    rail.addEventListener('scroll', () => {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(syncFromScroll);
    }, {passive: true});
    rail.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        centerCard(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
        startAutoRotate();
      }
    });
    section.addEventListener('pointerenter', () => { paused = true; });
    section.addEventListener('pointerleave', () => { paused = false; });
    section.addEventListener('focusin', () => { paused = true; });
    section.addEventListener('focusout', (event) => {
      if (!(event.relatedTarget instanceof Node) || !section.contains(event.relatedTarget)) paused = false;
    });
    reducedMotion.addEventListener?.('change', startAutoRotate);

    paintActive(0);
    window.requestAnimationFrame(() => {
      rail.scrollTo({left: 0, behavior: 'auto'});
      syncFromScroll();
    });
    startAutoRotate();
  }

  async function loadReviewShowcase(section) {
    const rail = section.querySelector('[data-case-rail]');
    const status = section.querySelector('[data-case-review-status]');
    const endpoint = section.dataset.reviewEndpoint;
    if (!rail || !endpoint) return;

    rail.querySelectorAll('[data-case-image], .case-slide--portfolio img').forEach(watchImageAspect);
    bindCaseInteractions(section, rail);

    try {
      const response = await fetch(`${endpoint}?limit=12`, {headers: {Accept: 'application/json'}});
      const payload = await response.json();
      if (!response.ok) throw new Error('Review showcase unavailable');
      const reviews = Array.isArray(payload.reviews) ? payload.reviews : [];
      const reviewCards = document.createDocumentFragment();
      reviews.forEach((review, index) => reviewCards.appendChild(renderReview(review, index)));
      rail.prepend(reviewCards);
      if (status) status.textContent = reviews.length
        ? `${reviews.length} customer reviews loaded.`
        : 'No customer photos are available yet.';
    } catch (_error) {
      if (status) status.textContent = 'Customer reviews are temporarily unavailable.';
    } finally {
      initCaseCarousel(section, rail);
    }
  }

  document.querySelectorAll('[data-case-showcase]').forEach(loadReviewShowcase);
})();
